import { firestoreWrapper } from './firestoreWrapper';
import { Project, Task, TaskComment, ActivityLog, UserNotificationPreferences } from '@/types/product-manager';
import { Timestamp } from 'firebase/firestore';
import { deleteTaskImages, deleteCommentImages } from './productManagerImageService';

const COLLECTIONS = {
  PROJECTS: 'pm_projects',
  TASKS: 'pm_tasks',
  COMMENTS: 'pm_task_comments',
  ACTIVITY: 'pm_activity_logs',
  NOTIFICATIONS: 'pm_notifications',
  USER_PREFERENCES: 'pm_user_notification_preferences'
};

// Project operations
export const getProjects = async (): Promise<Project[]> => {
  const querySnapshot = await firestoreWrapper.getDocs(
    firestoreWrapper.collection(COLLECTIONS.PROJECTS)
  );
  return querySnapshot.docs
    .filter(doc => doc.id !== '_metadata') // Filter out metadata documents
    .map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Project));
};

export const createProject = async (project: Omit<Project, 'id'>): Promise<string> => {
  const docRef = await firestoreWrapper.addDoc(
    firestoreWrapper.collection(COLLECTIONS.PROJECTS),
    project
  );
  
  // Log activity
  await createActivityLog({
    entityType: 'project',
    entityId: docRef.id,
    action: 'created',
    userId: project.createdBy,
    userEmail: project.createdBy,
    timestamp: Timestamp.now(),
    metadata: {
      projectName: project.name,
      projectType: project.type
    }
  });
  
  return docRef.id;
};

export const getProject = async (id: string): Promise<Project | null> => {
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.PROJECTS),
    id
  );
  const docSnap = await firestoreWrapper.getDoc(docRef);
  
  if (docSnap.exists()) {
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as Project;
  }
  
  return null;
};

// Task operations
export const getTasks = async (projectId?: string): Promise<Task[]> => {
  const collection = firestoreWrapper.collection(COLLECTIONS.TASKS);
  
  if (projectId) {
    const query = firestoreWrapper.query(
      collection,
      firestoreWrapper.where('projectId', '==', projectId)
    );
    const querySnapshot = await firestoreWrapper.getDocs(query);
    return querySnapshot.docs
      .filter(doc => doc.id !== '_metadata') // Filter out metadata documents
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Task));
  }
  
  const querySnapshot = await firestoreWrapper.getDocs(collection);
  return querySnapshot.docs
    .filter(doc => doc.id !== '_metadata') // Filter out metadata documents
    .map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Task));
};

export const createTask = async (task: Omit<Task, 'id'>): Promise<string> => {
  const docRef = await firestoreWrapper.addDoc(
    firestoreWrapper.collection(COLLECTIONS.TASKS),
    task
  );
  
  // Log activity
  await createActivityLog({
    entityType: 'task',
    entityId: docRef.id,
    action: 'created',
    userId: task.createdBy,
    userEmail: task.createdBy,
    timestamp: Timestamp.now(),
    metadata: {
      taskTitle: task.title,
      projectId: task.projectId,
      assignedTo: task.assignedTo
    }
  });
  
  return docRef.id;
};

export const getTask = async (id: string): Promise<Task | null> => {
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.TASKS),
    id
  );
  const docSnap = await firestoreWrapper.getDoc(docRef);
  
  if (docSnap.exists()) {
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as Task;
  }
  
  return null;
};

// Comment operations
export const getTaskComments = async (taskId: string): Promise<TaskComment[]> => {
  const collection = firestoreWrapper.collection(COLLECTIONS.COMMENTS);
  const query = firestoreWrapper.query(
    collection,
    firestoreWrapper.where('taskId', '==', taskId)
  );
  
  const querySnapshot = await firestoreWrapper.getDocs(query);
  const comments = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as TaskComment));
  
  // Sort comments by createdAt in JavaScript to avoid needing a Firestore index
  return comments.sort((a, b) => {
    const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt as any);
    const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt as any);
    return aTime.getTime() - bTime.getTime();
  });
};

export const createTaskComment = async (comment: Omit<TaskComment, 'id'>): Promise<string> => {
  const docRef = await firestoreWrapper.addDoc(
    firestoreWrapper.collection(COLLECTIONS.COMMENTS),
    comment
  );
  
  // Log activity
  const metadata: any = {
    taskId: comment.taskId
  };
  
  // Only add mentions if they exist
  if (comment.mentions && comment.mentions.length > 0) {
    metadata.mentions = comment.mentions;
  }
  
  // Only add parentCommentId if it exists
  if (comment.parentCommentId) {
    metadata.parentCommentId = comment.parentCommentId;
  }
  
  await createActivityLog({
    entityType: 'comment',
    entityId: docRef.id,
    action: 'created',
    userId: comment.userEmail,
    userEmail: comment.userEmail,
    timestamp: Timestamp.now(),
    metadata
  });
  
  return docRef.id;
};

export const updateTaskComment = async (id: string, updates: Partial<TaskComment>): Promise<void> => {
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.COMMENTS),
    id
  );
  await firestoreWrapper.updateDoc(docRef, updates);
};

export const deleteTaskComment = async (id: string): Promise<void> => {
  try {
    // Get comment data before deletion to access images
    const comment = await getTaskComment(id);
    
    // Delete associated images if they exist
    if (comment?.images && comment.images.length > 0) {
      await deleteCommentImages(comment.images);
    }
    
    // Delete the comment document
    const docRef = firestoreWrapper.doc(
      firestoreWrapper.collection(COLLECTIONS.COMMENTS),
      id
    );
    await firestoreWrapper.deleteDoc(docRef);
  } catch (error) {
    
    throw error;
  }
};

// Helper function to get a single comment
const getTaskComment = async (id: string): Promise<TaskComment | null> => {
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.COMMENTS),
    id
  );
  const docSnap = await firestoreWrapper.getDoc(docRef);
  
  if (docSnap.exists()) {
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as TaskComment;
  }
  
  return null;
};

// Activity log operations
export const getActivityLogs = async (entityId?: string, entityType?: string): Promise<ActivityLog[]> => {
  const collection = firestoreWrapper.collection(COLLECTIONS.ACTIVITY);
  
  if (entityId && entityType) {
    const query = firestoreWrapper.query(
      collection,
      firestoreWrapper.where('entityId', '==', entityId),
      firestoreWrapper.where('entityType', '==', entityType),
      firestoreWrapper.orderBy('timestamp', 'desc')
    );
    const querySnapshot = await firestoreWrapper.getDocs(query);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ActivityLog));
  }
  
  const query = firestoreWrapper.query(
    collection,
    firestoreWrapper.orderBy('timestamp', 'desc')
  );
  
  const querySnapshot = await firestoreWrapper.getDocs(query);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as ActivityLog));
};

export const createActivityLog = async (log: Omit<ActivityLog, 'id'>): Promise<string> => {
  const docRef = await firestoreWrapper.addDoc(
    firestoreWrapper.collection(COLLECTIONS.ACTIVITY),
    log
  );
  return docRef.id;
};

// Helper function to get current user email (you'll need to pass this from the calling component)
let currentUserEmail = '';
export const setCurrentUserEmail = (email: string) => {
  currentUserEmail = email;
};

export const updateProject = async (id: string, updates: Partial<Project>, userEmail?: string): Promise<void> => {
  // Get current project data to track changes
  const currentProject = await getProject(id);
  
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.PROJECTS),
    id
  );
  await firestoreWrapper.updateDoc(docRef, updates);
  
  // Log activity with changes
  if (currentProject) {
    const changes: Record<string, { from: any; to: any }> = {};
    Object.keys(updates).forEach(key => {
      const oldValue = (currentProject as any)[key];
      const newValue = (updates as any)[key];
      if (oldValue !== newValue) {
        // Only include changes where both values are defined, or where we're setting a value for the first time
        const fromValue = oldValue !== undefined ? oldValue : null;
        const toValue = newValue !== undefined ? newValue : null;
        changes[key] = { from: fromValue, to: toValue };
      }
    });
    
    if (Object.keys(changes).length > 0) {
      await createActivityLog({
        entityType: 'project',
        entityId: id,
        action: 'updated',
        userId: userEmail || currentUserEmail,
        userEmail: userEmail || currentUserEmail,
        timestamp: Timestamp.now(),
        changes,
        metadata: {
          projectName: currentProject.name
        }
      });
    }
  }
};

export const deleteProject = async (id: string, userEmail?: string): Promise<void> => {
  try {
    
    
    // Get project data before deletion for activity logging
    const project = await getProject(id);
    if (!project) {
      
      return;
    }
    
    // Get all tasks for this project
    const tasks = await getTasks(id);
    
    
    // Collect all images to delete
    const allImageUrls: string[] = [];
    
    // Use batch operations for better performance
    const batch = firestoreWrapper.writeBatch();
    let operationCount = 0;
    const MAX_BATCH_SIZE = 500; // Firestore batch limit
    
    // Collect all comments for all tasks (batch read)
    let totalCommentsDeleted = 0;
    for (const task of tasks) {
      // Add task images to deletion list
      if (task.images && task.images.length > 0) {
        allImageUrls.push(...task.images);
      }
      
      const comments = await getTaskComments(task.id);
      totalCommentsDeleted += comments.length;
      
      // Add comment deletions to batch and collect comment images
      for (const comment of comments) {
        // Add comment images to deletion list
        if (comment.images && comment.images.length > 0) {
          allImageUrls.push(...comment.images);
        }
        
        const commentRef = firestoreWrapper.doc(
          firestoreWrapper.collection(COLLECTIONS.COMMENTS),
          comment.id
        );
        batch.delete(commentRef);
        operationCount++;
        
        // Commit batch if approaching limit
        if (operationCount >= MAX_BATCH_SIZE - 10) {
          
          await batch.commit();
          operationCount = 0;
        }
      }
      
      // Add task deletion to batch
      const taskRef = firestoreWrapper.doc(
        firestoreWrapper.collection(COLLECTIONS.TASKS),
        task.id
      );
      batch.delete(taskRef);
      operationCount++;
      
      // Commit batch if approaching limit
      if (operationCount >= MAX_BATCH_SIZE - 10) {
        
        await batch.commit();
        operationCount = 0;
      }
    }
    
    // Delete all images (don't block on this)
    if (allImageUrls.length > 0) {
      
      deleteTaskImages(id, allImageUrls).catch(error => {
        
        // Don't throw - we don't want image deletion to block project deletion
      });
    }
    
    // Add project deletion to batch
    const projectRef = firestoreWrapper.doc(
      firestoreWrapper.collection(COLLECTIONS.PROJECTS),
      id
    );
    batch.delete(projectRef);
    operationCount++;
    
    // Commit final batch
    if (operationCount > 0) {
      
      await batch.commit();
    }
    
    
    
    // Log activity (separate from batch to avoid conflicts)
    await createActivityLog({
      entityType: 'project',
      entityId: id,
      action: 'deleted',
      userId: userEmail || currentUserEmail,
      userEmail: userEmail || currentUserEmail,
      timestamp: Timestamp.now(),
      metadata: {
        projectName: project.name,
        projectType: project.type,
        tasksDeleted: tasks.length,
        commentsDeleted: totalCommentsDeleted,
        imagesDeleted: allImageUrls.length
      }
    });
    
  } catch (error) {
    
    throw error;
  }
};

export const updateTask = async (id: string, updates: Partial<Task>, userEmail?: string): Promise<void> => {
  // Get current task data to track changes
  const currentTask = await getTask(id);
  
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.TASKS),
    id
  );
  await firestoreWrapper.updateDoc(docRef, updates);
  
  // Log activity with changes
  if (currentTask) {
    const changes: Record<string, { from: any; to: any }> = {};
    Object.keys(updates).forEach(key => {
      const oldValue = (currentTask as any)[key];
      const newValue = (updates as any)[key];
      if (oldValue !== newValue) {
        // Only include changes where both values are defined, or where we're setting a value for the first time
        const fromValue = oldValue !== undefined ? oldValue : null;
        const toValue = newValue !== undefined ? newValue : null;
        changes[key] = { from: fromValue, to: toValue };
      }
    });
    
    if (Object.keys(changes).length > 0) {
      // Determine the action type based on what changed
      let action = 'updated';
      if (changes.status?.to === 'completed') {
        action = 'completed';
      } else if (changes.assignedTo) {
        action = 'assigned';
      }
      
      await createActivityLog({
        entityType: 'task',
        entityId: id,
        action: action as any,
        userId: userEmail || currentUserEmail,
        userEmail: userEmail || currentUserEmail,
        timestamp: Timestamp.now(),
        changes,
        metadata: {
          taskTitle: currentTask.title,
          projectId: currentTask.projectId
        }
      });
    }
  }
};

export const deleteTask = async (id: string, userEmail?: string, logActivity: boolean = true): Promise<void> => {
  try {
    
    
    // Get task data before deletion for activity logging and image cleanup
    const task = await getTask(id);
    
    // Get all comments for this task
    const comments = await getTaskComments(id);
    
    
    // Collect all images to delete
    const allImageUrls: string[] = [];
    
    // Add task images
    if (task?.images && task.images.length > 0) {
      allImageUrls.push(...task.images);
    }
    
    // Add comment images
    for (const comment of comments) {
      if (comment.images && comment.images.length > 0) {
        allImageUrls.push(...comment.images);
      }
    }
    
    // Delete all images first (don't block on this)
    if (allImageUrls.length > 0) {
      
      deleteTaskImages(id, allImageUrls).catch(error => {
        
        // Don't throw - we don't want image deletion to block task deletion
      });
    }
    
    // Use batch operations for better performance
    const batch = firestoreWrapper.writeBatch();
    
    // Add comment deletions to batch
    for (const comment of comments) {
      const commentRef = firestoreWrapper.doc(
        firestoreWrapper.collection(COLLECTIONS.COMMENTS),
        comment.id
      );
      batch.delete(commentRef);
    }
    
    // Add task deletion to batch
    const taskRef = firestoreWrapper.doc(
      firestoreWrapper.collection(COLLECTIONS.TASKS),
      id
    );
    batch.delete(taskRef);
    
    // Commit batch
    await batch.commit();
    
    
    // Log activity if requested (separate from batch to avoid conflicts)
    if (logActivity && task) {
      await createActivityLog({
        entityType: 'task',
        entityId: id,
        action: 'deleted',
        userId: userEmail || currentUserEmail,
        userEmail: userEmail || currentUserEmail,
        timestamp: Timestamp.now(),
        metadata: {
          taskTitle: task.title,
          projectId: task.projectId,
          commentsDeleted: comments.length,
          imagesDeleted: allImageUrls.length
        }
      });
    }
  } catch (error) {
    
    // Re-throw the error so the UI can handle it
    throw error;
  }
};

// User notification preferences operations
export const getUserNotificationPreferences = async (userEmail: string): Promise<UserNotificationPreferences | null> => {
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.USER_PREFERENCES),
    userEmail
  );
  const docSnap = await firestoreWrapper.getDoc(docRef);
  
  if (docSnap.exists()) {
    return docSnap.data() as UserNotificationPreferences;
  }
  
  return null;
};

export const saveUserNotificationPreferences = async (preferences: UserNotificationPreferences): Promise<void> => {
  const docRef = firestoreWrapper.doc(
    firestoreWrapper.collection(COLLECTIONS.USER_PREFERENCES),
    preferences.email
  );
  await firestoreWrapper.setDoc(docRef, preferences);
};